import React, { FC } from 'react';

import cx from 'classnames';
import BlockContent from '@sanity/block-content-to-react';
import sanityImgUtil from 'utils/sanityImgUtil';
import sanityImgSrcSetUtil from 'utils/sanityImgSrcSetUtil';

import { Img } from 'components/base';
import { ArticleCalloutPortableText } from 'styled';

import { CalloutColorScheme } from '../../constants/ColorScheme';
import {
  ListItem,
  ExternalLink,
  InternalLink,
  BlockRenderer,
} from 'constants/PortableTextSerializer';

import { ArticleCallout as IArticleCallout } from 'types';

interface Props {
  articleCallout: IArticleCallout | null;
}

const ArticleCallout: FC<Props> = ({ articleCallout }) => {
  if (!articleCallout) {
    return null;
  }

  const {
    color,
    variant,
    infoSectionText,
    supportingSectionIsActive,
    supportingSectionText,
    supportingSectionImage,
    renderSupportingSectionWider,
  } = articleCallout;
  const colorScheme = CalloutColorScheme[color];
  const variantIsRight = variant === 'right';
  const supportingSectionTextIsActive = !!supportingSectionText.length;

  return (
    <div
      className={cx(
        `ArticleCallout bg-color-${colorScheme.infoSectionBackground} flex my1_5`,
        {
          'ArticleCallout--right right xl:flex-col xl:my0': variantIsRight,
          'ArticleCallout--bottom article-inner-text-full-width-md-up-extra-wide flex-row-reverse': !variantIsRight,
        }
      )}
    >
      <ArticleCalloutPortableText
        textColor={colorScheme.infoSectionText}
        className={cx(
          `ArticleCallout__info-section-container p1_5 md:p2_25 flex-grow`,
          {
            'ArticleCallout__info-section-container--narrower': renderSupportingSectionWider,
          }
        )}
      >
        {!!infoSectionText.length && (
          <BlockContent
            blocks={infoSectionText}
            serializers={{
              listItem: ListItem,
              marks: { link: ExternalLink, internalLink: InternalLink },
              types: { block: BlockRenderer },
            }}
          />
        )}
      </ArticleCalloutPortableText>
      {supportingSectionIsActive && (
        <ArticleCalloutPortableText
          textColor={colorScheme.supportingSectionText}
          className={cx(
            `ArticleCallout__supporting-section-container relative w100 bg-color-${colorScheme.supportingSectionBackground} p1_5 md:p2_25`,
            {
              'ArticleCallout__supporting-section-container--wider': renderSupportingSectionWider,
            }
          )}
        >
          {supportingSectionTextIsActive && (
            <BlockContent
              blocks={supportingSectionText}
              serializers={{
                listItem: ListItem,
                marks: { link: ExternalLink, internalLink: InternalLink },
                types: { block: BlockRenderer },
              }}
            />
          )}
          {!supportingSectionTextIsActive && supportingSectionImage.id && (
            <Img
              alt={supportingSectionImage.alt}
              className="ArticleCallout__image absolute"
              sizes="(maxWidth: 1024px) 350px, 600px"
              src={sanityImgUtil(supportingSectionImage, 350)}
              srcSet={sanityImgSrcSetUtil(
                supportingSectionImage,
                350,
                350,
                600
              )}
            />
          )}
        </ArticleCalloutPortableText>
      )}
    </div>
  );
};

export default ArticleCallout;
